/**
 * The composer's scholars step (spec 5.4, 5.5, 8.2) — Phase 2B, Task 12.
 *
 * A sibling file rather than more keys in `reviewRequests.composer.ts` for the
 * reason that file itself exists: it was already 352 lines against this repo's
 * 400-line hard limit before this step's copy, its blocked-fix links and its
 * three write-status buckets were written. Spread back into the SAME
 * `reviewRequests.requests` namespace from there, so every key below is
 * `reviewRequests.requests.scholars.*` and `@/locales/reviewRequests` stays
 * the one import path.
 *
 * The card's own copy — the badges, the reason clauses, the blocked messages
 * and the SSB untick control — deliberately stays in `reviewRequests.composer.ts`
 * beside `scholarCardFor`'s other keys. This file is the STEP around the cards:
 * who may be added, what a failed write says, and where a blocked scholar gets
 * fixed.
 *
 * Same two constraints as its siblings: English only, and `{named}`
 * placeholders only — `cspSafeMessageCompiler` cannot parse vue-i18n's `|`
 * plural syntax, so count-dependent text is a manual `_one` / `_other` pair.
 */
export const reviewRequestsScholarsMessages = {
  scholars: {
    title: "Who is reviewing?",
    // Says the one thing that makes the cards below readable: none of it was
    // typed. Spec 5.1 rung 4 — nothing is silently defaulted, because a
    // defaulted arrangement is a defaulted purchase order.
    intro:
      "Suhayl works out the arrangement and what each review is priced at from this client's board roster and the scholar's engagement terms. Nothing on these cards is typed by hand, and nothing is defaulted — every card says why it says what it says.",
    loadError:
      "The scholars for this request could not be loaded. Please try again.",
    // Not an error, and not a blank panel: the step opens here every time.
    none: "No scholars added yet. Add one below to see how their review will be priced.",
    addTitle: "Add a scholar",
    // The name is interpolated as TEXT. Scholar names are CSA-synced data and
    // there is no `v-html` anywhere in this module.
    add: "Add {name}",
    // Only the roster is offered. Asserting board membership the roster does
    // not know about is a data change, not a form entry (spec 5.4), so the
    // roster screen is the only place a scholar joins a client's board.
    rosterEmpty:
      "This client's board roster is empty. Add the scholars who sit on it before composing a request for them.",
    allAdded:
      "Every scholar on this client's roster is already on this request.",
    // The write LANDED and the re-read that redraws the cards did not. Says
    // both, because the cards below are now showing the arrangement and the
    // price from before the change, and only a reload fixes that.
    refreshFailed:
      "The change was saved, but these cards could not be refreshed. Reload the page before sending — what they show is out of date.",
    remove: "Remove {name}",

    // Where a blocked scholar gets fixed (`resolutionRules.ts::blockedFixFor`).
    // A refusal with no next step is what makes a SAM email support; each of
    // these names the screen that owns the missing fact. `no_documents` has no
    // link on purpose — see that function's doc comment.
    fixTerms: "Open this scholar's terms",
    fixRoster: "Open the roster",
    fixBands: "Open the category bands",
    // Said beside the link, because the guard is only half the answer: the SAM
    // has to know their place is kept. The draft is resumable from the
    // dashboard, but staying here keeps the step and the quota the scholars
    // already on it are holding (the stale-draft sweep releases it after 24
    // untouched hours).
    fixNewTab:
      "Opens in a new tab. This draft stays open here, with its place and the quota it is holding for the other scholars on it.",

    // Mapped from the STATUS each write answered with, never from its body:
    // only a 409's text survives `ApiService` (`reviewRequests.http.ts`), and
    // `put_draft_scholar` answers 422 to four different refusals whose bodies
    // are the server's own English. Each `unprocessable` below therefore names
    // every cause its route can have rather than guessing one — including the
    // roster going stale mid-composition, which shares the status and is the
    // only one that is not about this request at all.
    addStatus: {
      forbidden: "You do not have permission to change this request.",
      notFound: "This request could not be found. It may have been cancelled.",
      unprocessable:
        "This scholar could not be added. The request may already name the most scholars it can hold, it may no longer be a draft, or this client's roster may have changed since this screen was opened — reopen the composer to check.",
      fallback: "This scholar could not be added. Please try again.",
    },
    removeStatus: {
      forbidden: "You do not have permission to change this request.",
      notFound: "This scholar is no longer on this request.",
      unprocessable:
        "This scholar could not be removed. This request may no longer be a draft, or this client's roster may have changed since this screen was opened — reopen the composer to check.",
      fallback: "This scholar could not be removed. Please try again.",
    },
    untickStatus: {
      forbidden: "You do not have permission to change this request.",
      notFound: "This request could not be found. It may have been cancelled.",
      unprocessable:
        "Board membership could not be changed on this review. The reason may be too long, this request may no longer be a draft, or this client's roster may have changed since this screen was opened — reopen the composer to check.",
      fallback:
        "Board membership could not be changed on this review. Please try again.",
    },
  },
};
